/**
 * t374u: per-workspace branding. The product is **The Hub**; each company (workspace) shows its own name, logo and
 * colours. Stored in `collab.workspaces.settings.branding` (names, colours, asset ids); the logo files themselves are
 * in `collab.workspace_brand_assets` and served publicly by the API (they appear on the sign-in page). A workspace with
 * no branding shows The Hub's own (Concept C "Modules": slate and indigo). See docs/branding.md.
 */
import { type BrandColors } from './theme';
import { normalizeHex } from './color';

/** The product's name: what the service is called, whoever's company it is. */
export const PRODUCT_NAME = 'The Hub';
/** The product's domain (company subdomains are `<slug>.PRODUCT_DOMAIN`). */
export const PRODUCT_DOMAIN = 'thehubapp.co.uk';

/** The Hub's own colours (Concept C "Modules"): slate chrome, indigo accent. */
export const HUB_COLORS: BrandColors = { primary: '#0f172a', accent: '#4f46e5' };
/** Trade Outlet's colours (customer #1): black chrome, yellow #fff200 accent. Data now; kept for tests and the migration. */
export const TRADE_OUTLET_COLORS: BrandColors = { primary: '#0a0a0a', accent: '#fff200' };

export const BRAND_ASSET_SLOTS = ['logo_light', 'logo_dark', 'logo_mark', 'favicon', 'app_icon'] as const;
export type BrandAssetSlot = (typeof BRAND_ASSET_SLOTS)[number];
export const BRAND_ASSET_LABEL: Record<BrandAssetSlot, string> = {
  logo_light: 'Logo for light backgrounds', logo_dark: 'Logo for dark backgrounds', logo_mark: 'Square mark', favicon: 'Browser icon',
  app_icon: 'Home-screen icon',
};
export const BRAND_ASSET_TYPES = ['image/svg+xml', 'image/png', 'image/webp', 'image/jpeg'] as const;
export type BrandAssetType = (typeof BRAND_ASSET_TYPES)[number];
export const BRAND_ASSET_EXT: Record<BrandAssetType, string> = { 'image/svg+xml': 'svg', 'image/png': 'png', 'image/webp': 'webp', 'image/jpeg': 'jpg' };
/** Logos are small: 512 KB is plenty for an SVG or a 1024px PNG. */
export const BRAND_ASSET_MAX_BYTES = 512 * 1024;
/** An asset id: sha256 of the bytes and the extension ("<64 hex>.svg"). Content-addressed, so restoring is exact. */
export const BRAND_ASSET_ID = /^[0-9a-f]{64}\.(svg|png|webp|jpg)$/;

/** What's stored in workspaces.settings.branding. Every field is optional: unset means "The Hub default". */
export interface BrandingSettings {
  company_name?: string | null;
  short_name?: string | null;
  /** Legal entity for footers ("Trade Outlet Ltd"); falls back to company_name. */
  legal_name?: string | null;
  email_from_name?: string | null;
  primary_color?: string | null;
  accent_color?: string | null;
  logo_light?: string | null;
  logo_dark?: string | null;
  logo_mark?: string | null;
  favicon?: string | null;
  /** Square PNG (at least 180 px, 512 px best) for the home-screen / installed-app icon. */
  app_icon?: string | null;
}

/** Branding as the apps get it (public endpoint, /me and the settings page). */
export interface WorkspaceBranding {
  workspace_id: string | null;
  slug: string | null;
  /** False when the workspace has set nothing (The Hub's own look). */
  custom: boolean;
  product_name: string;
  company_name: string | null;
  short_name: string | null;
  legal_name: string | null;
  /** "Trade Outlet - The Hub", or "The Hub" with no company name: tab titles, sign-in, emails. */
  app_title: string;
  email_from_name: string | null;
  primary_color: string;
  accent_color: string;
  /** Asset URLs relative to the API base (".../v1"), or null for The Hub's built-in artwork. */
  assets: Record<BrandAssetSlot, string | null>;
  /** The stored settings (asset ids, not URLs), for the settings page. */
  settings: BrandingSettings;
  /** Changes whenever anything does; clients cache on it. */
  version: string;
}

/** "Trade Outlet - The Hub", or just "The Hub". */
export const appTitle = (company?: string | null) => (company?.trim() ? `${company.trim()} - ${PRODUCT_NAME}` : PRODUCT_NAME);
/** The company as running text ("from everyone at …"): its brand name, else the workspace name, else the product. */
export const companyName = (b: Pick<BrandingSettings, 'company_name'> | null | undefined, workspaceName?: string | null) =>
  b?.company_name?.trim() || workspaceName?.trim() || PRODUCT_NAME;
/** Footer line: "Trade Outlet - The Hub · Trade Outlet Ltd" (or just the product). */
export const brandFooter = (b: Pick<BrandingSettings, 'company_name' | 'legal_name'> | null | undefined) => {
  const legal = b?.legal_name?.trim() || b?.company_name?.trim();
  return legal ? `${appTitle(b?.company_name)} · ${legal}` : PRODUCT_NAME;
};

const TEXT_LIMITS: Record<'company_name' | 'short_name' | 'legal_name' | 'email_from_name', number> = {
  company_name: 80, short_name: 24, legal_name: 120, email_from_name: 80,
};

/** Read whatever is stored (tolerant of junk: anything invalid is ignored). */
export function readBranding(settings: unknown): BrandingSettings {
  const raw = (settings && typeof settings === 'object' ? (settings as Record<string, unknown>).branding : null) as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') return {};
  const out: BrandingSettings = {};
  for (const k of Object.keys(TEXT_LIMITS) as (keyof typeof TEXT_LIMITS)[]) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.trim().slice(0, TEXT_LIMITS[k]);
  }
  for (const k of ['primary_color', 'accent_color'] as const) { const v = normalizeHex(raw[k] as string); if (v) out[k] = v; }
  for (const k of BRAND_ASSET_SLOTS) { const v = raw[k]; if (typeof v === 'string' && BRAND_ASSET_ID.test(v)) out[k] = v; }
  return out;
}

/**
 * Check a PUT body. Strings are trimmed; empty or null clears a field (back to the default). Colours must be hex.
 * Asset fields take an id from POST .../branding/assets (or null to clear).
 */
export function validateBrandingPatch(patch: unknown): { value: BrandingSettings; errors: string[] } {
  const errors: string[] = [];
  const value: BrandingSettings = {};
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return { value, errors: ['Send the branding as an object.'] };
  const p = patch as Record<string, unknown>;
  const known = new Set<string>([...Object.keys(TEXT_LIMITS), 'primary_color', 'accent_color', ...BRAND_ASSET_SLOTS]);
  for (const k of Object.keys(p)) if (!known.has(k)) errors.push(`Unknown branding field "${k}".`);
  for (const [k, max] of Object.entries(TEXT_LIMITS) as [keyof typeof TEXT_LIMITS, number][]) {
    if (!(k in p)) continue;
    const v = p[k];
    if (v === null || (typeof v === 'string' && !v.trim())) { value[k] = null; continue; }
    if (typeof v !== 'string') { errors.push(`${k} must be text.`); continue; }
    const t = v.trim().replace(/\s+/g, ' ');
    if (t.length > max) errors.push(`${k} can be at most ${max} characters.`);
    // eslint-disable-next-line no-control-regex
    else if (/[<>\u0000-\u001f]/.test(t)) errors.push(`${k} can't contain < > or control characters.`);
    else value[k] = t;
  }
  for (const k of ['primary_color', 'accent_color'] as const) {
    if (!(k in p)) continue;
    const v = p[k];
    if (v === null || v === '') { value[k] = null; continue; }
    const hex = typeof v === 'string' ? normalizeHex(v) : null;
    if (!hex) errors.push(`${k} must be a hex colour like #4f46e5.`); else value[k] = hex;
  }
  for (const k of BRAND_ASSET_SLOTS) {
    if (!(k in p)) continue;
    const v = p[k];
    if (v === null || v === '') { value[k] = null; continue; }
    if (typeof v !== 'string' || !BRAND_ASSET_ID.test(v)) errors.push(`${k} must be an uploaded image id.`);
    else if (k === 'app_icon' && !v.endsWith('.png')) errors.push('The home-screen icon must be a PNG.');
    else value[k] = v;
  }
  return { value, errors };
}

/** Merge a validated patch into stored settings (nulls remove a field). */
export function mergeBranding(current: BrandingSettings, patch: BrandingSettings): BrandingSettings {
  const next: Record<string, unknown> = { ...current };
  for (const [k, v] of Object.entries(patch)) { if (v === null || v === undefined) delete next[k]; else next[k] = v; }
  return next as BrandingSettings;
}

/** The asset ids a branding refers to (for clean-up and access checks). */
export const brandingAssetIds = (b: BrandingSettings) => BRAND_ASSET_SLOTS.map(s => b[s]).filter((v): v is string => Boolean(v));

/** Small deterministic hash (FNV-1a) for the version tag; not security-relevant. */
export function brandingVersion(b: BrandingSettings, extra = '') {
  const s = JSON.stringify(Object.keys(b).sort().map(k => [k, (b as Record<string, unknown>)[k]])) + extra;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(36);
}

/**
 * Resolve stored settings into what the apps use. `assetUrl` turns an asset id into a URL path (the API's public
 * asset route); without one the ids are returned as they are.
 */
export function resolveBranding(
  ws: { id: string; slug?: string | null; name?: string | null } | null,
  b: BrandingSettings,
  assetUrl: (wsId: string, id: string) => string = (_w, id) => id,
): WorkspaceBranding {
  const assets = Object.fromEntries(BRAND_ASSET_SLOTS.map(s => [s, ws && b[s] ? assetUrl(ws.id, b[s]!) : null])) as Record<BrandAssetSlot, string | null>;
  return {
    workspace_id: ws?.id ?? null,
    slug: ws?.slug ?? null,
    custom: Object.keys(b).length > 0,
    product_name: PRODUCT_NAME,
    company_name: b.company_name ?? null,
    short_name: b.short_name ?? null,
    legal_name: b.legal_name ?? null,
    app_title: appTitle(b.company_name),
    email_from_name: b.email_from_name ?? null,
    primary_color: b.primary_color ?? HUB_COLORS.primary,
    accent_color: b.accent_color ?? HUB_COLORS.accent,
    assets,
    settings: b,
    version: brandingVersion(b, ws?.id ?? ''),
  };
}

/** The Hub's own branding (no workspace, or before one is known). */
export const DEFAULT_BRANDING: WorkspaceBranding = resolveBranding(null, {});

/** Trade Outlet's branding as data (the t374u migration stores the same; the logo id is its SVG's sha256). */
export const TRADE_OUTLET_BRANDING: BrandingSettings = {
  company_name: 'Trade Outlet', short_name: 'Trade Outlet', legal_name: 'Trade Outlet Ltd', email_from_name: 'Trade Outlet - The Hub',
  primary_color: TRADE_OUTLET_COLORS.primary, accent_color: TRADE_OUTLET_COLORS.accent,
};

/**
 * Which workspace a host name belongs to: `<slug>.<product domain>` gives the slug; any other host is looked up as a
 * custom domain. Returns null for the bare product domain, `app.`/`www.` and localhost.
 */
export function slugFromHost(host: string | null | undefined, productDomain = PRODUCT_DOMAIN): { slug: string } | { domain: string } | null {
  const h = (host ?? '').trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
  if (!h || h === 'localhost' || /^[\d.]+$/.test(h) || h.includes('[')) return null;
  const pd = productDomain.toLowerCase();
  if (h === pd) return null;
  if (h.endsWith(`.${pd}`)) {
    const sub = h.slice(0, -pd.length - 1);
    if (!sub || sub.includes('.') || RESERVED_SUBDOMAINS.has(sub)) return null;
    return { slug: sub };
  }
  return { domain: h };
}
/**
 * t401u: the product's own hosts (the bare domain and anything under it, e.g. app.thehubapp.co.uk). Their sign-in is
 * The Hub's, never a company's, unless the host is that company's own <slug> subdomain.
 */
export function isPlatformHost(host: string | null | undefined, productDomain = PRODUCT_DOMAIN): boolean {
  const h = (host ?? '').trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
  const pd = productDomain.toLowerCase();
  return h === pd || h.endsWith(`.${pd}`);
}
export const RESERVED_SUBDOMAINS = new Set(['www', 'app', 'api', 'admin', 'help', 'status', 'demo', 'mail', 'docs', 'static', 'assets']);
